import React, { useEffect, useState } from "react";
import { getUserFields, getProfileFields } from "../../formSchemas/userSchemas";
import { FieldConfig, FormProps } from "./FormComponent/types";

interface UserFormProps extends FormProps {
  fields?: FieldConfig[];
  initialData?: Record<string, any>;
  disabled?: boolean;
  profileMode?: boolean;
  hideButton?: boolean;
}

const UserForm: React.FC<UserFormProps> = ({
  fields,
  initialData,
  onSubmit,
  onCancel,
  disabled = false,
  profileMode = false,
  hideButton = false,
}) => {
  const schemaFields =
    fields || (profileMode ? getProfileFields : getUserFields);
  const [formData, setFormData] = useState<Record<string, any>>({});
  const [showPassword, setShowPassword] = useState<Record<string, boolean>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const isEdit = !!initialData?.id || !!initialData?.uid;

  useEffect(() => {
    if (initialData) {
      setFormData((prev) => ({
        ...prev,
        ...initialData,
      }));
    }
  }, [initialData]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;

    setFormData((prev) => ({ ...prev, [name]: value }));

    if (name === "password") {
      const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[\W_]).{8,}$/;

      if (value && !passwordRegex.test(value)) {
        setErrors((prev) => ({
          ...prev,
          password:
            "Password must be at least 8 characters and include uppercase, lowercase, number, and special character",
        }));
      } else {
        setErrors((prev) => ({ ...prev, password: "" }));
      }

      if (formData.confirmPassword && formData.confirmPassword !== value) {
        setErrors((prev) => ({
          ...prev,
          confirmPassword: "Passwords do not match",
        }));
      } else {
        setErrors((prev) => ({ ...prev, confirmPassword: "" }));
      }
    } else if (name === "confirmPassword") {
      if (value !== formData.password) {
        setErrors((prev) => ({
          ...prev,
          confirmPassword: "Passwords do not match",
        }));
      } else {
        setErrors((prev) => ({ ...prev, confirmPassword: "" }));
      }
    } else {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (Object.values(errors).some((err) => err)) return;

    if (formData.password !== formData.confirmPassword) {
      setErrors((prev) => ({
        ...prev,
        confirmPassword: "Passwords do not match",
      }));
      return;
    }

    try {
      if (onSubmit) await onSubmit(formData);
    } catch (err: any) {
      console.error("Form submit error:", err);
      alert(err.message || "Failed to submit form.");
    }
  };

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-4 overflow-auto">
      <div
        className={`grid gap-4 ${schemaFields.length > 8 ? "grid-cols-1 md:grid-cols-2" : "grid-cols-1"
          }`}
      >
        {schemaFields.map((field) => {
          const widthClass = schemaFields.length > 8 ? "w-full" : "w-72";
          return (
            <div key={field.name} className="flex w-full items-start">
              {/* Label on the left */}
              <label htmlFor={field.name} className="w-60 pt-2">
                {field.label}:
              </label>

              {/* Input and error stacked vertically */}
              <div className={`flex flex-col ${widthClass}`}>
                {field.type === "select" ? (
                  <select
                    name={field.name}
                    value={formData[field.name] || ""}
                    onChange={handleChange}
                    disabled={disabled || field.disabled}
                    required={field.required}
                    className={`p-1 border border-gray-600 rounded-md focus:outline-2 outline-blue-600 disabled:bg-gray-100`}
                  >
                    <option value="">Select</option>
                    {(field.options || []).map((opt) => (
                      <option key={opt} value={opt}>
                        {opt}
                      </option>
                    ))}
                  </select>
                ) : (
                  <div className="relative">
                    <input
                      type={
                        field.type === "password" && showPassword[field.name]
                          ? "text"
                          : field.type
                      }
                      name={field.name}
                      value={formData[field.name] || ""}
                      onChange={handleChange}
                      placeholder={field.placeholder}
                      disabled={disabled || field.disabled}
                      required={field.required}
                      className="w-full p-1 pr-10 border border-gray-600 rounded-md focus:outline-2 outline-blue-600 disabled:bg-gray-100"
                    />
                    {field.type === "password" && field.showTogglePassword && (
                      <button
                        type="button"
                        onClick={() =>
                          setShowPassword((prev) => ({
                            ...prev,
                            [field.name]: !prev[field.name],
                          }))
                        }
                        className="absolute top-2 right-2 text-sm text-blue-600"
                      >
                        {showPassword[field.name] ? "Hide" : "Show"}
                      </button>
                    )}
                  </div>
                )}

                {/* ✅ Error message below the input */}
                {errors[field.name] && (
                  <p className="text-sm text-red-600 mt-1">
                    {errors[field.name]}
                  </p>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {!hideButton && (
        <div className="flex gap-4 mt-2">
          <button
            type="submit"
            disabled={disabled || Object.values(errors).some(Boolean)}
            className="w-44 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:bg-gray-400"
          >
            {disabled ? "Saving..." : isEdit ? "Update" : "Save"}
          </button>
          <button
            type="button"
            disabled={disabled}
            onClick={onCancel}
            className="w-44 py-2 bg-gray-500 text-white rounded hover:bg-gray-600 disabled:bg-gray-400"
          >
            Cancel
          </button>
        </div>
      )}
    </form>
  );
};

export default UserForm;
